// D-062 placement grammar — the CLIENT's layout renderers (ASK-112 S4).
//
// The engine used to be told an ALGORITHM NAME ("browse-bin", "fan-spread") and reconstruct
// the geometry from per-fixture manifest numbers. D-062 replaces the name with a description
// of the placement itself:
//
//     socket = { domain, distribution, pose, accepts[], fit }
//
// so this module renders a socket from what it IS — a curve/point/surface (domain), a way of
// occupying it (distribution), and an axis mapping of the item onto it (pose) — instead of from
// what it was CALLED. That is the whole point of the slice: a fixture nobody has written a
// renderer for still renders, because `spline × run × edge-out` is four words the engine already
// knows how to draw, and a new fixture is data rather than code.
//
// PURE — numbers in, numbers out, no THREE and no DOM, so it unit-tests headlessly
// (`scripts/test-layout-grammar.mjs`) exactly like `_socket-layout.js` and `ws3/_placement-rules.js`.
// The THREE adapter is `engine/slot-layouts/grammar.js`; it resolves the domain into WORLD
// coordinates first, so everything here is one frame with no transform ambiguity.
//
// 📦 THIS FILE IS THE `@kapowx/layout-grammar` PACKAGE (ASK-151 §6.7 ruling 1, ASK-154 seam 2): moved out of
// kapowx's `engine/_layout-grammar.js` byte-for-byte (plus this note) so the GCG console's reviewer card seats
// sample products with the SAME implementation the shop does — one layout implementation, two consumers. The
// purity above is now a package gate (`packages/layout-grammar/test/package.test.mjs`), not a convention.
//
// ─── What comes in ───────────────────────────────────────────────────────────────────────────
//   socket    the catalogue socket: { domain, distribution, pose, accepts[], geometry, capacity,
//             capacitySource, grammarSource, ... }. `geometry.points` are resolved to world by
//             the adapter and handed in as `domain`.
//   domain    { kind: "point"|"spline"|"surface"|"volume", position?, points?, size?, box? } — WORLD metres
//             (a volume's `box` is fixture-local, with its world axes beside it — see `volumeFrame`).
//   approach  unit vector: the direction the customer looks FROM, i.e. the way an item's face
//             should point. The composer already computes it as `frontDir`.
//   registry  boot.productTypes[] — the ONE source for canonical frame, pose presets and the
//             type-level reference dimensions (S1's ruling; the engine schema names no product).
//   products  the curated items. Each may carry `dimensions {widthMm,heightMm,thicknessMm}`
//             measured at ingest — so a run packs by the ITEM, not by its type's average.
//
// ─── What goes out ───────────────────────────────────────────────────────────────────────────
//   placements[]: { index, position, basis:{x,y,z}, dims, pitch, dimsSource }
//   `basis` are the world directions of the item mesh's local X/Y/Z axes — the adapter turns
//   them into a quaternion. Poses are expressed as axis mappings, so orientation is a basis
//   change and never a pile of special cases.

export const GRAMMAR_DOMAINS       = Object.freeze(["point", "spline", "surface", "volume"]);
export const GRAMMAR_DISTRIBUTIONS = Object.freeze(["single", "run", "stack", "grid", "arc", "scatter"]);
// `fill` (the volume distribution) is named in D-062 and deliberately NOT built: no socket on the
// platform authors it, and a renderer nothing exercises is a guess that looks like a feature.
// `scatter` LEFT this list in ASK-121 (P2-RENDER) — the free-placement primitive is built below and
// unit-tested, so it is no longer a word we refuse. `volume` LEFT it in ASK-139 F-139an: the
// catalogue's `record-crate` authors the platform's first volume socket (`volume × stack × edge-out`,
// BIN_01), the feed seats records in it, and the renderer below draws them — D-062's discipline is
// that a word leaves this list when its renderer lands, and that is now. Only `stack` is built in a
// volume (the one combination anything authors); any other distribution there refuses BY NAME.
export const GRAMMAR_DEFERRED = Object.freeze(["fill"]);

const AXES = Object.freeze(["w", "h", "d"]);
const V = (x, y, z) => ({ x, y, z });
const add = (a, b) => V(a.x + b.x, a.y + b.y, a.z + b.z);
const scale = (a, k) => V(a.x * k, a.y * k, a.z * k);
const sub = (a, b) => V(a.x - b.x, a.y - b.y, a.z - b.z);
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a, b) => V(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
const len = (a) => Math.hypot(a.x, a.y, a.z);
const norm = (a) => { const l = len(a); return l > 1e-9 ? scale(a, 1 / l) : V(0, 0, 1); };
const UP = Object.freeze(V(0, 1, 0));
/** Rodrigues rotation of `v` about unit axis `k` — the one turn a surface placement can carry
 *  (`at.yaw`, about the region normal; every pose preset is yaw-free, D-064). */
const rotateAboutAxis = (v, k, ang) => (!ang ? v
	: add(add(scale(v, Math.cos(ang)), scale(cross(k, v), Math.sin(ang))), scale(k, dot(k, v) * (1 - Math.cos(ang)))));

// Deterministic jitter (never Math.random — a pile that re-rolls on reload is not a scene).
function makeJitter(seed) {
	let s = 0x811c9dc5;
	for (let i = 0; i < (seed || "").length; i++) { s ^= seed.charCodeAt(i); s = (s * 0x01000193) >>> 0; }
	return () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return (s / 0xffffffff) * 2 - 1; };
}

// ─── Registry ────────────────────────────────────────────────────────────────────────────────

/** boot.productTypes[] → id-keyed map. Accepts an already-keyed object too (probe/test callers). */
export function typeRegistry(productTypes) {
	const byId = new Map();
	if (Array.isArray(productTypes)) {
		for (const t of productTypes) if (t?.productTypeId) byId.set(t.productTypeId, t);
	} else if (productTypes && typeof productTypes === "object") {
		for (const [k, v] of Object.entries(productTypes)) if (v) byId.set(k, v);
	}
	return byId;
}

/** UE Gameplay Tags semantics: match by SEGMENT, never by string prefix. `product.media.print`
 *  takes `product.media.print.comic`; it must NOT take a future `product.media.printer-supplies`,
 *  which a bare startsWith() would silently mis-shelve. */
export function tagMatches(accept, tagPath) {
	if (!accept || !tagPath) return false;
	if (accept === tagPath) return true;
	return tagPath.startsWith(accept + ".");
}

/** Every registry type a socket's `accepts[]` routes to (ANY across entries — an item resolves to
 *  exactly one tagPath, so an all-of match across two paths could never match anything at all). */
export function typesAccepting(accepts, registry) {
	const list = Array.isArray(accepts) ? accepts : (accepts ? [accepts] : []);
	const out = [];
	for (const [id, entry] of registry) {
		if (!entry?.tagPath) continue;
		if (list.some((a) => tagMatches(a, entry.tagPath))) out.push(id);
	}
	return out;
}

/** Does a FEED LINE match an accepts list (ASK-126 S3.3 — the composer's per-kind eligibility,
 *  bridged the same way `admits` bridges: a socket's `accepts` are D-062 tag paths, a feed line
 *  carries a type ID, and comparing the two raw refuses every real item whose id isn't ALSO listed
 *  in the legacy `acceptedProductTypes`). Rules, in order:
 *    · empty/unknown accepts → true (nothing to filter against)
 *    · a line with NO type and NO tagPath → true — absent is "no opinion", and the resolver already
 *      decided this line seats somewhere (D-061 §5: the server decides what; this filter only
 *      guards against CROSS-TYPE mis-assignment, which an untyped line cannot be shown to be)
 *    · else: raw id match (legacy) OR tag match via the registry's typeId→tagPath OR the line's own
 *      tagPath — and the ACCEPT side resolves through the registry too: an accept naming a registry
 *      TYPE ID (an UMBRELLA like `decor-book`) covers every realisation under that type's tagPath.
 *      Measured on the plane (S5 PASS 1 gap 2): the shelf's `acceptedProductTypes` are the umbrella
 *      ids `decor-book`/`decor-bookend`, the pinned line is typed `brass-bookend`, and treating the
 *      accept as only-an-id-or-tagPath filtered a MET seat out of the composer's pool — the seat
 *      rode the feed and drew nothing. Reads type/tag only — never `sellable` (D-063 guard 4). */
export function lineMatchesAccepts(accepts, line, registry) {
	const list = Array.isArray(accepts) ? accepts.filter(Boolean) : [];
	if (!list.length) return true;
	const typeId = line?.type ?? line?.productType ?? line?.productTypeId ?? null;
	const tag = line?.tagPath ?? (typeId != null ? (registry?.get?.(typeId)?.tagPath ?? null) : null);
	if (typeId == null && tag == null) return true;
	return list.some((a) => {
		if (a === typeId) return true;
		if (tag != null && tagMatches(a, tag)) return true;
		const acceptTag = registry?.get?.(a)?.tagPath ?? null;
		return acceptTag != null && tag != null && tagMatches(acceptTag, tag);
	});
}

/** The type whose dimensions/poses stand in for "what this socket holds" — the accepted umbrella
 *  (`umbrella: true`) if there is one, else the first match. Same `accepts[0]`-reference convention
 *  the mint derives capacity with, so the two arithmetics can be compared rather than assumed equal. */
export function referenceType(socket, registry) {
	const ids = typesAccepting(socket?.accepts, registry);
	const umbrella = ids.find((id) => registry.get(id)?.umbrella);
	const chosen = umbrella || ids[0]
		// Legacy fallback: pre-grammar sockets carry mint-derived `acceptedProductTypes` and no tags.
		|| (Array.isArray(socket?.acceptedProductTypes) ? socket.acceptedProductTypes.find((t) => registry.has(t)) : null);
	return chosen ? { id: chosen, entry: registry.get(chosen), matched: ids } : { id: null, entry: null, matched: ids };
}

/** A pose preset is an axis mapping held in the PRODUCT-TYPE registry (S1 ruling) — never in the
 *  engine schema. `{ up, along, spacing }`: `up` = the item axis that lies along the domain normal,
 *  `along` = the item axis packed along the domain tangent. */
export function poseMapping(typeEntry, poseName) {
	const p = typeEntry?.poses?.[poseName];
	if (!p || !AXES.includes(p.up) || !AXES.includes(p.along) || p.up === p.along) return null;
	return { up: p.up, along: p.along, spacing: Number.isFinite(p.spacing) && p.spacing >= 1 ? p.spacing : 1 };
}

/** Mesh-local axis (x|y|z) for each item axis (w|h|d), from the type's canonical frame.
 *  `canonicalFrame.up` names the item axis that is the mesh's local +Y, `.face` the local +Z;
 *  whatever is left is local +X. Without a frame the comic-era convention (h up, d facing) holds —
 *  it is what every shipped product mesh is authored to. */
export function localAxisMap(typeEntry) {
	const up = typeEntry?.canonicalFrame?.up || "h";
	const face = typeEntry?.canonicalFrame?.face || "d";
	if (!AXES.includes(up) || !AXES.includes(face) || up === face) return { h: "y", d: "z", w: "x" };
	const rest = AXES.find((a) => a !== up && a !== face);
	return { [up]: "y", [face]: "z", [rest]: "x" };
}

/** The item's declared spine (ASK-147 §13.6): `canonicalFrame.spine` is a SIGNED item axis in the
 *  same vocabulary as `up`/`face` (`"-w"` = the spine sits at the item's −w end). Absent, malformed,
 *  or naming the up/face axis → null: the type has no spine and nothing changes. */
export function spineOf(typeEntry) {
	const m = /^([+-]?)([whd])$/.exec(typeEntry?.canonicalFrame?.spine ?? "");
	if (!m) return null;
	const up = typeEntry.canonicalFrame.up || "h", face = typeEntry.canonicalFrame.face || "d";
	return m[2] === up || m[2] === face ? null : { axis: m[2], sign: m[1] === "-" ? -1 : 1 };
}

// ─── Item dimensions ─────────────────────────────────────────────────────────────────────────

/** Per-SKU dimensions when they are believable, the type reference when they are not.
 *
 *  GCG (S3) found 24 live rows carrying METRES in the millimetre columns — inert while nothing read
 *  them, and in the direction of "yes" the moment something derives from them (a 10-micron comic
 *  advertises a lane for ~54,000 items). The test is deliberately NOT a per-axis plausibility band:
 *  a band rejects a 60 mm slipcased omnibus, which is a real comic. A unit error scales ALL THREE
 *  axes by the SAME factor, so that is what is detected — and a suspect row falls back to the type
 *  reference (today's number, the safe direction) rather than being trusted or dropped. */
/**
 * 🔴 D-074 (ASK-137 §75) — THREE NUMBERS, AND ONLY ONE OF THEM IS THE WORLD.
 *
 *   physical      what the thing really is, in the real world. The shopper is told this. Today's
 *                 `dimensions` IS this number and keeps its name on the wire.
 *   model         the measured real-scale bounds of the published GLB — what the mesh actually is.
 *   displayScale  the merchant's presentation multiplier (default 1.0).
 *
 * and **world = model × displayScale** — the size the shop must reason about. Fit, seats, capacity
 * and the render all read the world size, because they are all questions about the thing standing on
 * a table, not about the thing in a catalogue. A 0.9 m stone owl declared as a 0.3 m ornament fits
 * nothing, and judging it by `physical` is how it would be seated anyway.
 *
 * ADDITIVE AND INERT (D-061): `model` and `displayScale` are not on any feed shipped to date, so
 * until GCG stamps them this returns exactly what it returned before, by the same path. The wire
 * shape read here is the one §75 names — `model` beside `dimensions`, in the same millimetre columns
 * — and if GCG ships another spelling this is the one function to change.
 *
 * PRINT IS UNCHANGED, deliberately (§75): a comic's size is its physical size, the type reference is
 * the safe fallback the unit-error guard leans on, and a merchant scaling a comic to 3× is not a
 * feature anyone asked for. Content owns the per-type policy band; this reads the scale and applies
 * the one rule the ruling fixes — locked at 1.0 for print.
 */
function worldDims(product, typeEntry) {
	const m = product?.model || null;
	const mm = m && [m.widthMm, m.heightMm, m.thicknessMm].every((v) => Number.isFinite(v) && v > 0)
		? { w: m.widthMm / 1000, h: m.heightMm / 1000, d: m.thicknessMm / 1000 }
		: null;
	if (!mm) return null;                                   // no model stamped → today's behaviour
	const scale = displayScaleFor(product, typeEntry);
	return { w: mm.w * scale, h: mm.h * scale, d: mm.d * scale, source: "model", displayScale: scale };
}

/**
 * The presentation multiplier actually in force. 1.0 unless the listing says otherwise, and 1.0
 * regardless for a print type — the lock is a rule, not a default, so a feed that carries 2.0 on a
 * comic is ignored rather than honoured (§75: "locked 1.0 for print types").
 */
export function displayScaleFor(product, typeEntry) {
	const raw = Number(product?.displayScale);
	if (!Number.isFinite(raw) || raw <= 0) return 1;
	// A type that declares cover surfaces is print/media — the same discriminator the runtime uses to
	// decide what can be READ, so the two cannot disagree about what a comic is.
	const slots = typeEntry?.surfaceSlots;
	if (Array.isArray(slots) && slots.length) return 1;
	return raw;
}

export function itemDims(product, typeEntry) {
	// D-074: the world size wins when it is known, because fit/seats/capacity are world questions.
	const world = worldDims(product, typeEntry);
	if (world) return world;
	const ref = typeEntry?.dimensions || null;
	const refDims = ref && Number.isFinite(ref.w) ? { w: ref.w, h: ref.h, d: ref.d } : null;
	const raw = product?.dimensions || null;
	const mm = raw && [raw.widthMm, raw.heightMm, raw.thicknessMm].every((v) => Number.isFinite(v) && v > 0)
		? { w: raw.widthMm / 1000, h: raw.heightMm / 1000, d: raw.thicknessMm / 1000 }
		: null;
	if (!mm) return refDims ? { ...refDims, source: refDims ? "type" : "fallback" } : null;
	if (!refDims) return { ...mm, source: "sku" };
	const ratios = AXES.map((a) => mm[a] / refDims[a]);
	const spread = Math.max(...ratios) / Math.min(...ratios);
	const geoMean = Math.cbrt(ratios[0] * ratios[1] * ratios[2]);
	// EVERY axis off in the SAME direction by the same order of magnitude ⇒ the columns hold the
	// wrong unit. A real product is off on ONE axis (a 60 mm omnibus is 12× the reference thickness
	// and bang on in width and height — spread 12, geo-mean 2.3, believed). GCG's live rows are
	// ~1000× down on all three at once — spread 2, geo-mean 0.0013, refused.
	const sameOrder = spread < 10;
	const wild = geoMean < 0.1 || geoMean > 10;
	if (sameOrder && wild) return { ...refDims, source: "type", suspect: { reason: "unit-error", ratio: +geoMean.toFixed(5) } };
	return { ...mm, source: "sku" };
}

/** The span one item occupies along the packing axis: `dimensions[axis] × spacing` (D-062). */
export function packingSpan(dims, axis, spacing) {
	const d = dims?.[axis];
	return Number.isFinite(d) && d > 0 ? d * (Number.isFinite(spacing) ? spacing : 1) : 0;
}

/**
 * ASK-150 §3.4 (SEAM B) — the point `s` metres along a MULTI-segment polyline, walked by ARC-LENGTH (Unreal spline
 * components distribute by distance along the spline), and the direction of the segment it lands on. `fromTail`
 * walks from the last point back (the run's flipped, customer-nearest start). Past the end it extrapolates along
 * the last segment, as the chord walk did past its end. Only ever called for 3+ points: a two-point lane keeps
 * its chord arithmetic byte-for-byte.
 */
export function polylineAt(points, s, fromTail = false) {
	const pts = fromTail ? [...points].reverse() : points;
	let at = null;
	for (let i = 1, acc = 0; i < pts.length; i++) {
		const seg = sub(pts[i], pts[i - 1]), L = len(seg);
		if (!(L > 0)) continue;
		const dir = scale(seg, 1 / L);
		at = { position: add(pts[i - 1], scale(dir, s - acc)), dir };
		if (s <= (acc += L)) break;
	}
	return at;
}

/** A distance along a spline frame: the polyline walk on a bent lane, the chord (main's bytes) on a straight one. */
const splineAt = (frame, d) => frame.points?.length > 2 ? polylineAt(frame.points, d).position : add(frame.origin, scale(frame.tangent, d));

// ─── Domain ──────────────────────────────────────────────────────────────────────────────────

/** Domain → a placement frame: an origin, a tangent to pack along, a normal to stand on, and the
 *  extent available. World coordinates; the adapter has already composed catalogue-local points. */
export function domainFrame(domain, approach) {
	const kind = domain?.kind;
	const face = norm(approach || V(0, 0, 1));
	if (kind === "point") {
		// A point domain has no extent of its own; its tangent is the horizontal axis across the
		// approach, which is the direction a fan spreads and a short row grows along.
		const t = norm(cross(UP, face));
		return { kind, origin: domain.position || V(0, 0, 0), tangent: t, normal: UP, length: 0, points: [domain.position || V(0, 0, 0)] };
	}
	if (kind === "spline") {
		const pts = (domain.points || []).filter((p) => p && Number.isFinite(p.x));
		if (pts.length < 2) return null;
		let length = 0;
		for (let i = 1; i < pts.length; i++) length += len(sub(pts[i], pts[i - 1]));
		// ASK-147 §12.5 — a LEANING tier stands its items on its own up. The mint publishes the socket's
		// local +Y as `geometry.normal` when it leans more than 1° off world up (absent = world up, so every
		// shipped spline — the longbox lane, the wall shelf — composes byte-identically); the adapter
		// rotates it into world. The same word the surface frame reads (below), one concept per domain.
		// `face-out`'s `up` maps onto it and poseBasis resolves the cover toward the approach, so a comic on
		// H2's 15° tier leans back onto the board instead of standing proud of it (§12.1: drawn 0°, want 15°).
		const n = domain.normal ? norm(domain.normal) : UP;
		return { kind, origin: pts[0], tangent: norm(sub(pts[pts.length - 1], pts[0])), normal: len(n) > 1e-9 ? n : UP, length, points: pts };
	}
	if (kind === "surface") {
		// A REGION: the mint's derived support-top polygon (ASK-121 §5 — emitted in catalogue-local
		// metres alongside today's spline polyline), or the synthetic `{size:{w,d}}` rectangle the grid
		// tests use. Either way the frame below carries the socket's OWN (u,v) axes, because `at:{u,v}`
		// is metres in that frame (INTERFACE 2026-08-26 correction) and every consumer must agree
		// about which way u points.
		// ── The MINT's emit shape (measured on live boot `ca6ca8e`, 2026-09-01): an axis-aligned
		//    `region {uMin,uMax,vMin,vMax}` in the socket's own metres, MINUS a list of `blocked`
		//    rectangles. Content chose the complement deliberately and it is the right choice twice
		//    over: a round top is four corner HOLES inside its bounding rectangle where a positive
		//    cover would need a staircase of strips, and holes are precisely what the packing
		//    literature calls DEFECTS — which MAXRECTS already takes for free (ASK-121 item 2).
		//
		//    The adapter has already named these in (u,v), so there is no projection here at all:
		//    the mint measures in fixture-local X/Z and `{u,v}` IS fixture-local X/Z (INTERFACE
		//    2026-08-26). Two descriptions of one frame is how a lane ends up where the box isn't,
		//    so there is only one.
		if (domain.region && domain.uAxis && domain.vAxis && domain.uvOrigin) {
			const r = domain.region;
			if (!(r.uMax > r.uMin) || !(r.vMax > r.vMin)) return null;
			const polygonUV = [
				{ u: r.uMin, v: r.vMin }, { u: r.uMax, v: r.vMin },
				{ u: r.uMax, v: r.vMax }, { u: r.uMin, v: r.vMax },
			];
			const blockedUV = (Array.isArray(domain.blocked) ? domain.blocked : [])
				.filter((b) => b && b.uMax > b.uMin && b.vMax > b.vMin);
			const nn = norm(domain.normal || UP);
			const axes = { u: norm(domain.uAxis), v: norm(domain.vAxis) };
			const origin = add(add(domain.uvOrigin, scale(axes.u, r.uMin)), scale(axes.v, r.vMin));
			const holeArea = blockedUV.reduce((a, b) => a + (b.uMax - b.uMin) * (b.vMax - b.vMin), 0);
			return {
				kind, origin, uvOrigin: domain.uvOrigin, tangent: axes.u, bitangent: axes.v,
				uAxis: axes.u, vAxis: axes.v, normal: nn,
				length: r.uMax - r.uMin, width: r.vMax - r.vMin,
				polygonUV, blockedUV, bounds: { uMin: r.uMin, uMax: r.uMax, vMin: r.vMin, vMax: r.vMax },
				// The FREE ground, holes removed — the number a capacity claim has to be answerable
				// against. The bounding rectangle's area would flatter every region with a till in it.
				area: (r.uMax - r.uMin) * (r.vMax - r.vMin) - holeArea,
				// `convex` describes the BOUNDARY, and the mint's boundary is a rectangle — always
				// convex, holes or not. Reporting `false` here because the region has holes conflated
				// two different facts and cost a real defect: the concave path edge-tests candidates
				// against the boundary polygon, and a footprint sitting flush against the edge of the
				// table registered as CROSSING it, so the counter packed zero items. Holes are
				// `blockedUV` and are answered by the defect pass; the boundary is answered here.
				convex: isConvexUV(polygonUV),
				regionExact: domain.regionExact !== false,
				points: polygonUV.map((p) => add(add(domain.uvOrigin, scale(axes.u, p.u)), scale(axes.v, p.v))),
			};
		}
		const ring = surfaceRing(domain);
		if (!ring) return null;
		let n = domain.normal ? norm(domain.normal) : polygonNormal(ring);
		if (!(len(n) > 1e-9)) return null;
		n = norm(n);
		// ORIENT the normal from what the surface IS, never from the winding — vertex order is an
		// authoring accident and a flipped normal would stand every item on its head. A support-top
		// faces up; a wall faces the customer.
		if (Math.abs(dot(n, UP)) >= 0.5) { if (dot(n, UP) < 0) n = scale(n, -1); }
		else if (dot(n, face) < 0) n = scale(n, -1);

		// The (u,v) axes are the SOCKET's, supplied by the THREE adapter as the fixture's own local
		// +X/+Z rotated into world (so a pin travels with its fixture — D-061 host-local, one layer
		// down). Absent an adapter (pure tests) they are derived from the plane alone, which for a
		// level top is exactly world +X / +Z.
		const axes = (domain.uAxis && domain.vAxis)
			? { u: norm(domain.uAxis), v: norm(domain.vAxis) }
			: surfaceAxes(n);
		// uvOrigin = the socket's frame origin projected onto the plane, so `{u:0,v:0}` is the
		// fixture's own centreline and not some corner that moves when the top is re-cut.
		const uvOrigin = domain.uvOrigin || scale(n, dot(ring[0], n));
		const polygonUV = ring.map((p) => {
			const d = sub(p, uvOrigin);
			return { u: dot(d, axes.u), v: dot(d, axes.v) };
		});
		const us = polygonUV.map((p) => p.u), vs = polygonUV.map((p) => p.v);
		const bounds = { uMin: Math.min(...us), uMax: Math.max(...us), vMin: Math.min(...vs), vMax: Math.max(...vs) };
		// `origin` stays the region's (uMin,vMin) corner and `tangent`/`bitangent` stay the marching
		// axes, so `grid` — written before this rewrite — reads exactly what it read before.
		const origin = add(add(uvOrigin, scale(axes.u, bounds.uMin)), scale(axes.v, bounds.vMin));
		return {
			kind, origin, uvOrigin, tangent: axes.u, bitangent: axes.v, uAxis: axes.u, vAxis: axes.v,
			normal: n, length: bounds.uMax - bounds.uMin, width: bounds.vMax - bounds.vMin,
			polygonUV, bounds, area: polygonAreaUV(polygonUV), convex: isConvexUV(polygonUV), points: ring,
		};
	}
	if (kind === "volume") return volumeFrame(domain, face);
	return null;
}

/**
 * 🔴 ASK-139 F-139an — THE VOLUME DOMAIN: a measured BOX the stock stands inside.
 *
 * The mint emits it as two corner empties (`DOMAIN_<id>_0/1`, composed to fixture-local by the
 * catalogue: record-crate BIN_01 = x ±0.37 · y 0.47‥0.85 · z ±0.22, the inside of the bin measured
 * off the served GLB). The adapter hands the box over in FIXTURE-LOCAL metres together with the
 * fixture's own axes turned into world (`uAxis` = local +X, `vAxis` = local +Z, `normal` = local +Y,
 * `uvOrigin` = local 0,0,0) — the surface region's convention, so one transform describes both.
 *
 * Which horizontal axis is FRONT-TO-BACK is the approach's question, never the box's: the box axis
 * most nearly along the approach is the depth the run marches through (away from the customer,
 * starting at the wall nearest them, as the spline run does), the other is ACROSS — numbered left
 * to right as the customer stands there. A box has no other preferred direction, so a crate turned
 * 90° re-derives the same answer instead of packing sideways.
 *
 * Frame: `origin` = the front-left-bottom corner (world) · `tangent` = depth direction (away) ·
 * `bitangent` = across (customer's right) · `normal` = up · `length`/`width`/`height` = depth /
 * across / height in metres · `corners` = the eight world corners, so an instrument can ask "is this
 * item inside the bin" against the same box the layout used.
 */
export function volumeFrame(domain, approach) {
	const b = domain?.box;
	if (!b || ![b.xMin, b.xMax, b.yMin, b.yMax, b.zMin, b.zMax].every(Number.isFinite)) return null;
	if (!(b.xMax > b.xMin) || !(b.yMax > b.yMin) || !(b.zMax > b.zMin)) return null;
	const N = norm(domain.normal || UP);
	let X = norm(domain.uAxis || V(1, 0, 0)), Z;
	if (domain.vAxis) Z = norm(domain.vAxis);
	else {
		// ASK-147 §14.4 — a socket-frame box (a tilted bin) arrives with its normal and uAxis only; the
		// third axis is the socket's local +Z = uAxis × normal (+X × +Y), so the frame stays right-handed
		// and the socket-local corners land where the socket put them. uAxis is squared to the normal
		// first: the mint rounds both to 6 dp.
		X = norm(sub(X, scale(N, dot(X, N))));
		Z = norm(cross(X, N));
	}
	const O = domain.uvOrigin || V(0, 0, 0);
	const at = (x, y, z) => add(add(add(O, scale(X, x)), scale(N, y)), scale(Z, z));
	const face = norm(approach || V(0, 0, 1));
	const depthIsZ = Math.abs(dot(Z, face)) >= Math.abs(dot(X, face));
	// The front wall is the one the approach points OUT of; depth runs from it, away from the customer.
	const dAxis = depthIsZ ? Z : X, aAxis = depthIsZ ? X : Z;
	const frontPositive = dot(dAxis, face) > 0;
	const depthDir = scale(dAxis, frontPositive ? -1 : 1);
	// Across, left → right as the customer sees it: they look along −face, so their right is (−face) × up.
	const right = norm(cross(scale(face, -1), N));
	const acrossPositive = dot(aAxis, right) >= 0;
	const acrossDir = scale(aAxis, acrossPositive ? 1 : -1);
	const dMin = depthIsZ ? b.zMin : b.xMin, dMax = depthIsZ ? b.zMax : b.xMax;
	const aMin = depthIsZ ? b.xMin : b.zMin, aMax = depthIsZ ? b.xMax : b.zMax;
	const front = frontPositive ? dMax : dMin;
	const left = acrossPositive ? aMin : aMax;
	const local = (d, a, y) => (depthIsZ ? at(a, y, d) : at(d, y, a));
	const corners = [];
	for (const x of [b.xMin, b.xMax]) for (const y of [b.yMin, b.yMax]) for (const z of [b.zMin, b.zMax]) corners.push(at(x, y, z));
	const length = dMax - dMin, width = aMax - aMin, height = b.yMax - b.yMin;
	const origin = local(front, left, b.yMin);
	const mid = add(origin, scale(acrossDir, width / 2));
	return {
		kind: "volume", origin, tangent: depthDir, bitangent: acrossDir, normal: N,
		length, width, height, box: { ...b }, corners,
		// The run's centreline, front to back along the floor — the spline-shaped answer to "where is
		// this domain" for anything that reads `points` (the adapter's lane extras, instruments).
		points: [mid, add(mid, scale(depthDir, length))],
	};
}

// ─── Surface regions — the free-placement primitive (ASK-121) ────────────────────────────────
//
// D-062 names `surface` as a domain and ASK-121 builds it. Three pieces, in the order they compose:
// the REGION (a polygon and its own (u,v) frame), ADMISSION (`fit`, D-064), and PACKING (MAXRECTS
// with the merchant's pins pre-inserted as defects). Everything here is 2D in the region's own
// (u,v) metres; the only 3D step is the last one, mapping (u,v) back to a world position.

/** The region's boundary ring in world metres, from either authored shape. */
function surfaceRing(domain) {
	const poly = Array.isArray(domain?.polygon) && domain.polygon.length >= 3 ? domain.polygon
		: (Array.isArray(domain?.points) && domain.points.length >= 3 ? domain.points : null);
	if (poly) return poly.filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z));
	// The synthetic `{position, size:{w,d}}` rectangle — a level top, expressed as a ring so there is
	// ONE region code path rather than a rectangle special case that drifts from the polygon one.
	const size = domain?.size;
	if (!size || !Number.isFinite(size.w) || !Number.isFinite(size.d)) return null;
	const c = domain.position || V(0, 0, 0);
	const { u, v } = surfaceAxes(domain.normal ? norm(domain.normal) : UP);
	const hw = size.w / 2, hd = size.d / 2;
	return [
		add(add(c, scale(u, -hw)), scale(v, -hd)), add(add(c, scale(u, hw)), scale(v, -hd)),
		add(add(c, scale(u, hw)), scale(v, hd)), add(add(c, scale(u, -hw)), scale(v, hd)),
	];
}

/** Newell's area-weighted polygon normal — stable for a slightly non-planar ring and independent of
 *  which vertex the author happened to start at. Its SIGN follows the winding, so callers re-orient. */
function polygonNormal(pts) {
	let n = V(0, 0, 0);
	for (let i = 0; i < pts.length; i++) {
		const a = pts[i], b = pts[(i + 1) % pts.length];
		n = add(n, V((a.y - b.y) * (a.z + b.z), (a.z - b.z) * (a.x + b.x), (a.x - b.x) * (a.y + b.y)));
	}
	return n;
}

/**
 * The region's own in-plane axes, derived from the plane alone so every consumer computes the same
 * pair. For a LEVEL surface (a table top, a floor zone) they are the local +X and +Z — which is what
 * "metres in the socket's own local frame" means for a horizontal region, and why a merchant's
 * `{u,v}` reads as "across" and "back" rather than as an arbitrary rotation. For a VERTICAL surface
 * (a wall panel) u is the horizontal in-plane axis and v points up.
 */
export function surfaceAxes(normal) {
	const n = norm(normal);
	if (Math.abs(dot(n, UP)) >= 0.5) {
		const u = norm(sub(V(1, 0, 0), scale(n, dot(V(1, 0, 0), n))));
		return { u, v: norm(cross(u, n)) };          // level: (+X, +Z) when n is +Y
	}
	const u = norm(cross(UP, n));
	return { u, v: norm(cross(n, u)) };              // vertical: (horizontal, up)
}

/**
 * The region's frame computed in the polygon's OWN coordinates — what the THREE adapter needs, so it
 * can rotate the axes into world rather than re-deriving them there from world geometry.
 *
 * This is the load-bearing half of "a pinned item moves with its fixture" (INTERFACE 2026-08-26,
 * the socket-local address corollary): u and v are the FIXTURE's own +X/+Z, so `{u: 0.2, v: 0}` is
 * 200 mm across THAT table however the merchant has since rotated it. Deriving the axes from world
 * geometry instead would silently re-point every pin the first time a fixture was turned.
 *
 * @returns {{ normal, uAxis, vAxis, uvOrigin }} all in the polygon's own frame, or null
 */
export function surfaceLocalFrame(polygon, face = null) {
	const ring = Array.isArray(polygon) ? polygon.filter((p) => p && Number.isFinite(p.x)) : [];
	if (ring.length < 3) return null;
	let n = polygonNormal(ring);
	if (!(len(n) > 1e-9)) return null;
	n = norm(n);
	if (Math.abs(dot(n, UP)) >= 0.5) { if (dot(n, UP) < 0) n = scale(n, -1); }
	else if (face && dot(n, norm(face)) < 0) n = scale(n, -1);
	const { u, v } = surfaceAxes(n);
	return { normal: n, uAxis: u, vAxis: v, uvOrigin: scale(n, dot(ring[0], n)) };
}

/** Signed area of a (u,v) ring — |area| is the region's true size, which is what a capacity claim
 *  has to be answerable against. */
export function polygonAreaUV(uv) {
	let a = 0;
	for (let i = 0; i < uv.length; i++) { const p = uv[i], q = uv[(i + 1) % uv.length]; a += p.u * q.v - q.u * p.v; }
	return Math.abs(a) / 2;
}

/** Convex? Decided once per region, because containment can be corner-tested on a convex region and
 *  needs edge testing on a concave one — and a packer that does not KNOW which is a packer that is
 *  guessing. */
export function isConvexUV(uv) {
	let sign = 0;
	for (let i = 0; i < uv.length; i++) {
		const a = uv[i], b = uv[(i + 1) % uv.length], c = uv[(i + 2) % uv.length];
		const z = (b.u - a.u) * (c.v - b.v) - (b.v - a.v) * (c.u - b.u);
		if (Math.abs(z) < 1e-12) continue;
		const s = z > 0 ? 1 : -1;
		if (sign === 0) sign = s; else if (s !== sign) return false;
	}
	return true;
}

/** Point-in-region, ray-casting (handles concave rings; a boundary point counts as inside). */
export function insideUV(uv, u, v, eps = 1e-9) {
	let inside = false;
	for (let i = 0, j = uv.length - 1; i < uv.length; j = i++) {
		const a = uv[i], b = uv[j];
		if ((a.v > v + eps) !== (b.v > v + eps)) {
			const x = ((b.u - a.u) * (v - a.v)) / ((b.v - a.v) || 1e-12) + a.u;
			if (u < x) inside = !inside;
		}
	}
	if (inside) return true;
	// On the edge (within eps) is IN: a footprint sized to the largest inscribed rectangle would
	// otherwise fail its own admission test by a rounding error.
	for (let i = 0, j = uv.length - 1; i < uv.length; j = i++) {
		const a = uv[i], b = uv[j];
		const du = b.u - a.u, dv = b.v - a.v, l2 = du * du + dv * dv;
		const t = l2 > 0 ? Math.max(0, Math.min(1, ((u - a.u) * du + (v - a.v) * dv) / l2)) : 0;
		if (Math.hypot(u - (a.u + t * du), v - (a.v + t * dv)) <= eps * 1e3) return true;
	}
	return false;
}

/** Does an axis-aligned (u,v) rect lie wholly inside the region? Corners for a convex region; corners
 *  plus edge-crossing tests for a concave one, because a concave notch can bite a rect whose four
 *  corners are all inside. */
export function rectInsideUV(uv, r, convex) {
	const corners = [[r.u, r.v], [r.u + r.w, r.v], [r.u + r.w, r.v + r.h], [r.u, r.v + r.h]];
	for (const [cu, cv] of corners) if (!insideUV(uv, cu, cv)) return false;
	if (convex) return true;
	for (let i = 0, j = uv.length - 1; i < uv.length; j = i++) {
		if (segmentCrossesRect(uv[j], uv[i], r)) return false;
	}
	return true;
}

/**
 * Is this `{u,v}` on the FREE ground of a region — inside the boundary AND not on a blocked patch?
 *
 * The two halves are not the same question and must not be one branch. Outside the boundary is "off
 * the table"; on a `blocked` rect is "on the table, but there is a cash register there" (measured:
 * `wooden-checkout-counter` carries 7 of them, `round-bar-stool` 6). Both refuse a drop, and the
 * merchant needs the difference to make sense of the refusal.
 */
export function frameContains(frame, u, v) {
	if (!frame?.polygonUV) return false;
	if (!insideUV(frame.polygonUV, u, v)) return false;
	for (const b of (frame.blockedUV || [])) {
		if (u > b.uMin && u < b.uMax && v > b.vMin && v < b.vMax) return false;
	}
	return true;
}

function segmentCrossesRect(a, b, r) {
	const edges = [
		[{ u: r.u, v: r.v }, { u: r.u + r.w, v: r.v }], [{ u: r.u + r.w, v: r.v }, { u: r.u + r.w, v: r.v + r.h }],
		[{ u: r.u + r.w, v: r.v + r.h }, { u: r.u, v: r.v + r.h }], [{ u: r.u, v: r.v + r.h }, { u: r.u, v: r.v }],
	];
	const cw = (p, q, s) => (q.u - p.u) * (s.v - p.v) - (q.v - p.v) * (s.u - p.u);
	for (const [c, d] of edges) {
		const d1 = cw(a, b, c), d2 = cw(a, b, d), d3 = cw(c, d, a), d4 = cw(c, d, b);
		if (((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0))) return true;
	}
	return false;
}

/** A (u,v) coordinate in the region's frame → a WORLD position on the plane. */
export function uvToWorld(frame, u, v) {
	const o = frame?.uvOrigin || frame?.origin || V(0, 0, 0);
	return add(add(o, scale(frame.uAxis || frame.tangent, u)), scale(frame.vAxis || frame.bitangent, v));
}

/** …and back: a world point → the region's (u,v). What a drop under the cursor resolves to. */
export function worldToUV(frame, position) {
	const d = sub(position, frame?.uvOrigin || frame?.origin || V(0, 0, 0));
	return { u: dot(d, frame.uAxis || frame.tangent), v: dot(d, frame.vAxis || frame.bitangent) };
}

// ─── `fit` — D-064 admission, in metres, yaw-tolerant ────────────────────────────────────────
//
// `fit = { maxFootprint: [x, z], maxHeight: y, clearance }`, derived at gate/mint from the same
// single measurement that emits `surfaces[]`. It is the ONLY thing standing between a table whose
// `accepts` roots at the tag tree and a piano (ASK-121 §the piano), so it is enforced here rather
// than described. Admission tests 0° AND 90°, because every pose preset is yaw-free (D-064) and a
// 300×200 item on a 250×250 top is a real fit that a single-orientation test would refuse.

/** Breathing room around each item, in metres. Derived (`fit.clearance`, D-064) beats an authored
 *  param, for the same reason capacity is derived: the mint measured it off the geometry. */
export function surfaceClearance(socket) {
	const f = socket?.fit?.clearance;
	if (Number.isFinite(f) && f >= 0) return f;
	const p = socket?.params?.clearance;
	return Number.isFinite(p) && p >= 0 ? p : 0;
}

/** Item footprint (the two axes that are not the pose's `up`) in metres: `[along, third]`. */
export function footprintOf(dims, pose) {
	const third = AXES.find((x) => x !== pose?.up && x !== pose?.along);
	return { fu: dims?.[pose?.along] ?? 0, fv: dims?.[third] ?? 0, up: dims?.[pose?.up] ?? 0 };
}

/**
 * Would `fit` admit an item of these dimensions?
 * @returns {{ok:true, yaw:0|number}} | {{ok:false, reason:"no-fit", detail}}
 *          `yaw` is the turn (radians about the region normal) admission needed — 0 or π/2.
 */
export function fitAdmits(fit, dims, pose) {
	if (!fit) return { ok: true, yaw: 0 };
	const { fu, fv, up } = footprintOf(dims, pose);
	const mf = Array.isArray(fit.maxFootprint) ? fit.maxFootprint : null;
	const eps = 1e-6;
	if (Number.isFinite(fit.maxHeight) && up > fit.maxHeight + eps) {
		return { ok: false, reason: "no-fit", detail: `${(up * 1000).toFixed(0)} mm tall, headroom is ${(fit.maxHeight * 1000).toFixed(0)} mm` };
	}
	if (!mf || !Number.isFinite(mf[0]) || !Number.isFinite(mf[1])) return { ok: true, yaw: 0 };
	if (fu <= mf[0] + eps && fv <= mf[1] + eps) return { ok: true, yaw: 0 };
	if (fv <= mf[0] + eps && fu <= mf[1] + eps) return { ok: true, yaw: Math.PI / 2 };   // turned a quarter
	return { ok: false, reason: "no-fit", detail: `${(fu * 1000).toFixed(0)}×${(fv * 1000).toFixed(0)} mm won't fit ${(mf[0] * 1000).toFixed(0)}×${(mf[1] * 1000).toFixed(0)} mm, either way round` };
}

/**
 * Does this socket's `accepts` root at the tag tree — i.e. is it the broadest thing the platform can
 * express? D-064: such a socket MUST carry `fit`, or the *"a broad product table never receives a
 * piano"* promise is an accident of every live `accepts` currently being narrow.
 *
 * Decided by MEASUREMENT rather than by matching a root token: an `accepts` that routes to every type
 * the registry knows IS the root, whatever it is spelled. That keeps the client honest about a root
 * whose name is Content's to choose (ASK-121 scope 0z deliberately does not hard-code `["product"]`).
 */
export function rootsAtTagRoot(socket, registry) {
	if (!registry || registry.size === 0) return false;
	const list = Array.isArray(socket?.accepts) ? socket.accepts : [];
	if (!list.length) return false;
	return typesAccepting(list, registry).length === registry.size;
}

// ─── MAXRECTS with the merchant's pins pre-inserted (ASK-121 item 2) ─────────────────────────
//
// The literature calls pre-placed obstacles DEFECTS, and MAXRECTS generalises to them for free:
// initialise the free-rectangle list as the whole region, insert the pinned footprints FIRST, then
// run the normal loop — its free-region update rule is indifferent to *why* a region is occupied.
// (Jylänki, *A thousand ways to pack the bin*; juj/RectangleBinPack; secnot/rectpack. Guillotine does
// NOT adapt: arbitrary obstacles are not guillotine-separable.)
//
// Leftover policy is `dense` — backfill the gaps a pin creates, the retail default and CSS Grid's
// `grid-auto-flow: dense` (ASK-120 ruling 7). Best-Short-Side-Fit IS dense by construction: it always
// takes the tightest gap an item fits. `sparse` is named headroom and is refused by name below rather
// than silently rendered as dense.

const overlaps = (a, b) => a.u < b.u + b.w - 1e-9 && b.u < a.u + a.w - 1e-9 && a.v < b.v + b.h - 1e-9 && b.v < a.v + a.h - 1e-9;

/** MAXRECTS' split rule: replace a free rect that a used rect intrudes on with the (up to four)
 *  maximal rectangles left over. */
function splitFree(free, used) {
	if (!overlaps(free, used)) return [free];
	const out = [];
	if (used.u > free.u) out.push({ u: free.u, v: free.v, w: used.u - free.u, h: free.h });
	if (used.u + used.w < free.u + free.w) out.push({ u: used.u + used.w, v: free.v, w: free.u + free.w - (used.u + used.w), h: free.h });
	if (used.v > free.v) out.push({ u: free.u, v: free.v, w: free.w, h: used.v - free.v });
	if (used.v + used.h < free.v + free.h) out.push({ u: free.u, v: used.v + used.h, w: free.w, h: free.v + free.h - (used.v + used.h) });
	return out;
}

const contains = (a, b) => a.u <= b.u + 1e-9 && a.v <= b.v + 1e-9 && a.u + a.w >= b.u + b.w - 1e-9 && a.v + a.h >= b.v + b.h - 1e-9;

function prune(rects) {
	const out = [];
	for (let i = 0; i < rects.length; i++) {
		if (rects[i].w <= 1e-9 || rects[i].h <= 1e-9) continue;
		let swallowed = false;
		for (let j = 0; j < rects.length && !swallowed; j++) if (i !== j && contains(rects[j], rects[i]) && !(contains(rects[i], rects[j]) && j > i)) swallowed = true;
		if (!swallowed) out.push(rects[i]);
	}
	return out;
}

function occupy(free, used) {
	let next = [];
	for (const f of free) next.push(...splitFree(f, used));
	return prune(next);
}

/**
 * ASK-128 S3 — the SAME free-region subtraction, exported so the merch-zone derivation reuses it
 * rather than growing a second opinion about "what space is left".
 *
 * A zone's derivation is `floor − Σ(footprint ⊕ clearance) − Σ(exclusions) − agentRadius carve-back`,
 * and that is precisely MAXRECTS' obstacle rule one level up: the packer subtracts a till standing on
 * a counter, the zone subtracts a shelf standing on the floor. Ruling 3's lesson — one parse, one
 * place — applied to geometry: a second rect-subtraction is how the overlay a merchant SEES and the
 * space the packer USES come to disagree.
 *
 * @param free [{u,v,w,h}] the current free list (start with the whole region)
 * @param used {u,v,w,h}   the obstacle to remove
 * @returns [{u,v,w,h}] MAXIMAL free rectangles — they may OVERLAP each other (that is what maximal
 *          means, and what makes "the biggest thing that still fits" a one-line read). For a
 *          disjoint decomposition — drawing, or measuring area — do not use this list.
 */
export function subtractRect(free, used) { return occupy(free, used); }

/**
 * Pack footprints into a region, around whatever is pinned.
 *
 * @param frame     a `surface` domain frame (carries `polygonUV`, `bounds`, `convex`)
 * @param items     [{ key, fu, fv }] — footprints in region metres, in the order they should be tried
 * @param pins      [{ key, u, v, yaw }] — merchant-placed, honoured exactly; `u,v` is the item's
 *                  base-centre, the same point a placement reports
 * @param clearance metres of breathing room between neighbours AND from the region edge
 * @returns {{ placed:[{key,u,v,yaw,fu,fv,pinned}], refused:[{key,reason}] }}
 */
export function packScatter({ frame, items = [], pins = [], clearance = 0 } = {}) {
	const placed = [], refused = [];
	if (!frame || frame.kind !== "surface" || !frame.polygonUV) return { placed, refused: items.map((it) => ({ key: it.key, reason: "no-region" })) };
	const uv = frame.polygonUV, convex = frame.convex !== false;
	const b = frame.bounds;
	// The region inset by the clearance is what is actually packable; each footprint is grown by the
	// same amount so neighbour gaps and edge gaps come out equal. One number, two jobs, no surprises.
	let free = [{ u: b.uMin + clearance, v: b.vMin + clearance, w: (b.uMax - b.uMin) - 2 * clearance, h: (b.vMax - b.vMin) - 2 * clearance }];
	if (free[0].w <= 0 || free[0].h <= 0) return { placed, refused: items.map((it) => ({ key: it.key, reason: "no-fit" })) };

	// THE MINT'S HOLES ARE DEFECTS, AND THEY GO IN FIRST — before any pin, before any auto item.
	// MAXRECTS' free-region update rule is indifferent to *why* a region is occupied, which is the
	// whole reason ASK-121 item 2 calls this "a commodity, not research": a till in the middle of a
	// counter and a comic the merchant pinned there are the same fact to the packer.
	for (const b of (frame.blockedUV || [])) {
		free = occupy(free, { u: b.uMin - clearance, v: b.vMin - clearance, w: (b.uMax - b.uMin) + 2 * clearance, h: (b.vMax - b.vMin) + 2 * clearance });
	}
	// TOUCHING IS NOT OVERLAPPING, and the epsilon is load-bearing rather than defensive: the free
	// list is built by SUBTRACTING these same rectangles, so a candidate MAXRECTS just carved a home
	// for sits exactly flush against the boundary it carved. Measured with a strict `<`, a bar-counter
	// packed ZERO items — every candidate was rejected by 2e-17 of float error against the rule that
	// had just made room for it. Same 1e-9 the free-list `overlaps` test uses, so the two agree.
	const EPS = 1e-9;
	const clearsHoles = (r) => !(frame.blockedUV || []).some((b) =>
		r.u < b.uMax + clearance - EPS && b.uMin - clearance + EPS < r.u + r.w
		&& r.v < b.vMax + clearance - EPS && b.vMin - clearance + EPS < r.v + r.h);
	const fitsRegion = (r) => rectInsideUV(uv, { u: r.u - clearance, v: r.v - clearance, w: r.w + 2 * clearance, h: r.h + 2 * clearance }, convex) && clearsHoles(r);

	// ── Pins first. A pin is honoured where the merchant put it, or refused BY NAME — never nudged:
	//    a pin that quietly slid 40 mm is the platform overruling the merchant without saying so.
	const pinnedRects = [];
	for (const p of pins) {
		const it = items.find((x) => x.key === p.key);
		if (!it) continue;
		const yaw = Number.isFinite(p.yaw) ? p.yaw : 0;
		const turned = Math.abs(Math.abs(yaw % Math.PI) - Math.PI / 2) < 1e-6;
		const fu = turned ? it.fv : it.fu, fv = turned ? it.fu : it.fv;
		const r = { u: p.u - fu / 2, v: p.v - fv / 2, w: fu, h: fv };
		if (!fitsRegion(r)) { refused.push({ key: p.key, reason: "no-such-seat" }); continue; }
		if (pinnedRects.some((q) => overlaps(q, { u: r.u - clearance, v: r.v - clearance, w: r.w + 2 * clearance, h: r.h + 2 * clearance }))) { refused.push({ key: p.key, reason: "no-fit" }); continue; }
		pinnedRects.push(r);
		placed.push({ key: p.key, u: p.u, v: p.v, yaw, fu, fv, pinned: true });
		free = occupy(free, { u: r.u - clearance, v: r.v - clearance, w: r.w + 2 * clearance, h: r.h + 2 * clearance });
	}

	// ── Then the auto tiers, packed AROUND the reservations. Best-Short-Side-Fit: the gap that leaves
	//    the least slack on its tighter axis, ties broken on the long side — Jylänki's BSSF, the
	//    variant that scores best on the free-form instances this is (mixed sizes, no shelf structure).
	const done = new Set(placed.map((p) => p.key));
	for (const it of items) {
		if (done.has(it.key)) continue;
		let best = null;
		for (const f of free) {
			for (const [fu, fv, yaw] of [[it.fu, it.fv, 0], [it.fv, it.fu, Math.PI / 2]]) {
				if (fu > f.w + 1e-9 || fv > f.h + 1e-9) continue;
				const short = Math.min(f.w - fu, f.h - fv), long = Math.max(f.w - fu, f.h - fv);
				const cand = { u: f.u, v: f.v, w: fu, h: fv };
				if (!fitsRegion(cand)) continue;
				if (!best || short < best.short - 1e-12 || (Math.abs(short - best.short) <= 1e-12 && long < best.long)) {
					best = { short, long, u: f.u, v: f.v, fu, fv, yaw };
				}
				if (Math.abs(it.fu - it.fv) < 1e-9) break;      // a square needs no second orientation
			}
		}
		if (!best) { refused.push({ key: it.key, reason: "no-fit" }); continue; }
		placed.push({ key: it.key, u: best.u + best.fu / 2, v: best.v + best.fv / 2, yaw: best.yaw, fu: best.fu, fv: best.fv, pinned: false });
		free = occupy(free, { u: best.u - clearance, v: best.v - clearance, w: best.fu + 2 * clearance, h: best.fv + 2 * clearance });
	}
	return { placed, refused };
}

// ─── Pose → basis ────────────────────────────────────────────────────────────────────────────

/** |third · approach| at or below this is PERPENDICULAR — no near side for a spine to turn toward. */
const SPINE_EPS = 1e-6;

/** World directions of the item mesh's local X/Y/Z for one pose on one domain frame.
 *  `alongDir` is the direction the item's `pose.along` axis points; `normalDir` its `pose.up`.
 *
 *  When the item's FACE axis is neither of those two (face-out on a shelf: up=h, along=w, face=d),
 *  the pose alone leaves a 180° ambiguity — and the wrong half of it shows a shop full of back
 *  covers. It is resolved by turning the item about its up axis until the face points at the
 *  customer, which flips `along` with it (a rotation, never a mirror: the basis stays right-handed). */
export function poseBasis({ typeEntry, pose, normalDir, alongDir, faceDir }) {
	const localOf = localAxisMap(typeEntry);      // item axis (w|h|d) → mesh local axis (x|y|z)
	const faceAxis = typeEntry?.canonicalFrame?.face || "d";
	const n = norm(normalDir);
	const a0 = norm(alongDir);
	const third = AXES.find((x) => x !== pose.up && x !== pose.along);
	// 🔴 RIGHT-HANDED, ALWAYS — the promise three lines up, which this used to keep only by luck of
	// which pose axis happened to be `third`.
	//
	// `cross(n, a)` is the correct third direction for a pose whose `third` maps to local X (edge-out:
	// up=h→y, along=d→z, rest=w→x — there z === cross(x, y) already). For a pose whose `third` maps to
	// local Z it is the NEGATIVE of what right-handedness needs: base-down is up=h→y, along=w→x,
	// face=d→z, so z must be cross(x, y) = cross(a, n) = −cross(n, a). It was not, so the basis was a
	// REFLECTION, `Matrix4.makeBasis` built a mirror, and `setFromRotationMatrix` on a mirror returns a
	// non-unit, meaningless quaternion.
	//
	// MEASURED on the deployed plane: every item on a derived `surface` socket carried the IDENTICAL
	// quaternion {x:-0.1228, y:0, z:0, w:0.6964} — norm 0.707, no turn about the vertical at all — so
	// a comic the packer had turned 90° to make it fit was drawn square, overlapping its neighbour.
	// base-down is the pose EVERY derived surface socket uses; edge-out (the longbox, the only shipped
	// grammar socket) was already right-handed, which is why nothing caught it until a second pose
	// existed.
	//
	// The sign is settled on the DERIVED axis, never on `up` or `along` which the pose authored, so a
	// basis that was already right-handed comes back byte-identical.
	const build = (aa) => {
		const mk = (t) => {
			const dirs = { [pose.up]: n, [pose.along]: aa, [third]: t };
			const b = { x: V(1, 0, 0), y: V(0, 1, 0), z: V(0, 0, 1) };
			for (const axis of AXES) b[localOf[axis]] = dirs[axis];
			return b;
		};
		let t = norm(cross(n, aa));
		let b = mk(t);
		if (dot(cross(b.x, b.y), b.z) < 0) { t = scale(t, -1); b = mk(t); }
		return { basis: b, faceDirOut: t };
	};
	// The 180° face disambiguation is evaluated against the FINAL third direction — where the cover
	// actually ends up — rather than against `cross(n, a)`, which is that direction only half the
	// time. Same rule, now asked of the answer instead of an intermediate.
	let out = build(a0);
	if (faceDir && faceAxis === third && dot(out.faceDirOut, norm(faceDir)) < 0) out = build(scale(a0, -1));
	// ASK-147 §13.6 — the same 180° rule, asked of the declared SPINE: edge-out on a shelf (up=h,
	// along=d, third=w) left the spine free, and `cross(n, a)` put it against the wall on every book.
	// Only where the third axis actually points at or away from the customer: the longbox's run lies
	// ALONG the approach, its third axis crosses it (dot 0), and its comics stay sideways, unturned.
	const spine = spineOf(typeEntry);
	if (faceDir && spine && spine.axis === third) {
		const toward = spine.sign * dot(out.faceDirOut, norm(faceDir));
		if (toward < -SPINE_EPS) out = build(scale(a0, -1));
	}
	const basis = out.basis;
	return basis;
}

// ─── Capacity ────────────────────────────────────────────────────────────────────────────────

/** Capacity per D-062's own table, from the domain the socket declares — never authored.
 *  Returned alongside `authored` so a divergence from the mint's shim is VISIBLE: if the two
 *  arithmetics drift, a merchant reads one number in the catalogue and sees another on the shelf. */
export function derivedCapacity({ frame, distribution, pose, dims, socket }) {
	const spanAlong = packingSpan(dims, pose?.along, pose?.spacing);
	const spanUp    = packingSpan(dims, pose?.up, pose?.spacing);
	if (distribution === "single") return 1;
	if (distribution === "run") {
		if (frame.kind === "spline" && spanAlong > 0) return Math.max(0, Math.floor(frame.length / spanAlong));
		// A point domain has no length of its own — its authored count is the capacity (the
		// back-compat case D-062 §back-compat names: today's point sockets ≡ point + count layouts).
		return Number.isFinite(socket?.capacity) ? socket.capacity : 0;
	}
	if (distribution === "stack" && frame.kind === "volume") {
		// D-062 clause 1 for the volume: columns ACROSS × rows FRONT-TO-BACK × layers UP, each from the
		// item's own extent on that axis — the record-crate holds two columns of LPs, ~73 deep at the
		// edge-out 1.2 spacing, one layer (a 320 mm sleeve in a 380 mm bin).
		const g = volumeGrid(frame, dims, pose);
		return g.cols * g.layers * (spanAlong > 0 ? Math.max(0, Math.floor(frame.length / spanAlong)) : 0);
	}
	if (distribution === "stack") {
		if (Number.isFinite(frame.height) && spanUp > 0) return Math.max(0, Math.floor(frame.height / spanUp));
		return Number.isFinite(socket?.capacity) ? socket.capacity : 0;
	}
	if (distribution === "arc") return Number.isFinite(socket?.capacity) ? socket.capacity : 3;
	if (distribution === "scatter") {
		// D-062 clause 1 for the surface domain: capacity is how many REFERENCE footprints the region
		// actually takes, answered by running the real packer rather than by dividing areas — an area
		// quotient over-promises on any region that is not a multiple of the footprint, and a capacity
		// that over-promises is what sends the resolver items the shelf cannot show.
		if (frame.kind !== "surface" || !(spanAlong > 0)) return 0;
		const { fu, fv } = footprintOf(dims, pose);
		if (!(fu > 0) || !(fv > 0)) return 0;
		const clearance = surfaceClearance(socket);
		const room = ((frame.bounds.uMax - frame.bounds.uMin) - 2 * clearance) * ((frame.bounds.vMax - frame.bounds.vMin) - 2 * clearance);
		// Area over footprint is a strict UPPER bound on how many can fit, so it is a safe loop cap:
		// the packer decides the answer, this only stops an unbounded loop on a degenerate region.
		const ceiling = Math.min(4096, Math.max(0, Math.ceil(room / (fu * fv)) + 1));
		const items = Array.from({ length: ceiling }, (_, i) => ({ key: i, fu, fv }));
		return packScatter({ frame, items, clearance }).placed.length;
	}
	if (distribution === "grid") {
		if (frame.kind !== "surface" || !(spanAlong > 0)) return Number.isFinite(socket?.capacity) ? socket.capacity : 0;
		// A panel (ASK-147 §5.4): rows by the item's UP extent, never its thickness — the wall-rack
		// read 993 here when the face-out pose's third axis (a 5 mm sleeve) pitched the rows.
		const panel = panelFrame(frame);
		if (panel) {
			const g = panelGrid(panel, dims, pose, panelGridGap(socket));
			return g.cols * g.rows;
		}
		const across = packingSpan(dims, AXES.find((x) => x !== pose.up && x !== pose.along), pose?.spacing);
		const cols = Math.max(0, Math.floor(frame.length / spanAlong));
		const rows = across > 0 ? Math.max(0, Math.floor(frame.width / across)) : 0;
		return cols * rows;
	}
	return 0;
}

/** ASK-147 §13.6 — capacity across the ACCEPTED types: the smallest count any accepted umbrella
 *  derives, i.e. the reference is the one with the LARGEST packing span, on every domain — so the
 *  number a merchant reads is one the holder can always hold. The mint's `capacityAcross` takes the
 *  same set (accepted umbrellas) and the same minimum, so the two halves agree. "First umbrella" sized
 *  a print bookcase by its 5 mm floppy (146 a shelf where 29 hardcovers fit, §13.4). A socket with one
 *  accepted type, or none that is an umbrella, is the reference alone — unchanged by construction. */
export function capacityAcross({ frame, distribution, poseName, socket, registry, ref, refPose, refDims }) {
	const umbrellas = (ref?.matched || []).filter((id) => registry.get(id)?.umbrella);
	let min = derivedCapacity({ frame, distribution, pose: refPose, dims: refDims, socket });
	for (const id of umbrellas) {
		if (id === ref.id) continue;
		const entry = registry.get(id);
		const pose = poseMapping(entry, poseName), dims = itemDims(null, entry);
		// A type that cannot take this pose, or that the socket's `fit` refuses, can never seat here —
		// so it has no say in what the holder holds (a tag-root region would otherwise read 0 off a piano).
		if (!pose || !dims || !fitAdmits(socket?.fit, dims, pose).ok) continue;
		const n = derivedCapacity({ frame, distribution, pose, dims, socket });
		if (Number.isFinite(n) && !(n >= min)) min = n;
	}
	return min;
}

/** The volume's cell grid for one reference item: how many columns stand ACROSS the box (the item's
 *  third axis — neither up nor packed), how many layers rise UP it, and each column's width. Zero
 *  columns or layers = the item does not fit the box at all, which the caller names. */
export function volumeGrid(frame, dims, pose) {
	const third = AXES.find((x) => x !== pose?.up && x !== pose?.along);
	const across = dims?.[third] ?? 0, up = dims?.[pose?.up] ?? 0;
	const cols = across > 0 ? Math.floor(frame.width / across + 1e-9) : 0;
	const layers = up > 0 ? Math.floor(frame.height / up + 1e-9) : 0;
	return { cols, layers, cellWidth: cols > 0 ? frame.width / cols : 0, layerHeight: up, third };
}

// ─── The PANEL — a surface that is not level (ASK-147 §5.4) ──────────────────────────────────
//
// Everything above treats a surface as a TOP: its normal is up, a pose's `up` lands on that normal
// and the grid marches across it. A wall bay is not a top. Measured through this renderer on the
// vinyl-wall-rack's real socket (ASK-147 §5.3): every sleeve stood straight out of the panel with
// its cover facing the floor, and the bay claimed 993 records instead of 15 — because the pose's
// `up` went onto the panel normal, the rows pitched by sleeve THICKNESS, and each sleeve hovered in
// the middle of its cell.
//
// Coord's seam (ASK-147 §5.4) is a frame the panel carries separately from its normal:
//   n     = the panel normal (toward the customer — the region frame has already oriented it)
//   up    = world-up projected onto the panel ("up the slope"); when |n·worldUp| > 0.9 the surface
//           is LEVEL, there is no panel, and every level path stays exactly as it was
//   right = the in-plane axis to the customer's right as they face the panel. Coord's text writes
//           it `n × up`; that is the customer's LEFT in this right-handed, Y-up world (n=+Z, up=+Y →
//           −X), so the frame is right-handed (right, up, n) instead: right = up × n — the same axis
//           `surfaceAxes` already calls `u` on a vertical surface, so a pin's u keeps its meaning.
// On a panel a pose's `up` maps onto `up` and the face lands on +n with no new pose word; grid rows
// advance along `up`, columns along `right`, each cell is the item's extent on that axis plus the
// gap, and an item stands on the BOTTOM of its cell (gravity is −up on the panel).

/** |n·worldUp| above this is a LEVEL surface — a top, never a panel (Coord, ASK-147 §5.4). */
export const LEVEL_DOT = 0.9;

/** The air between neighbouring items on a panel grid, metres — the gap list's 1 cm (row H1) that
 *  Content's builder derives the bay from (3 × 0.33 m + 1 cm). A socket may author `params.gap`. */
export const PANEL_GRID_GAP = 0.01;

/** The gap a panel grid leaves between items: the socket's own `params.gap`, else the default. */
export function panelGridGap(socket) {
	const g = socket?.params?.gap;
	return Number.isFinite(g) && g >= 0 ? g : PANEL_GRID_GAP;
}

/**
 * The panel frame of a surface domain frame, or null when the surface is level (or not a surface).
 * `origin` is the panel's bottom-left corner as the customer sees it; `width` runs along `right`,
 * `height` along `up` (up the slope — 1.66 m on the vinyl-wall-rack, not its 1.65 m rise).
 *
 * @returns {{ normal, up, right, origin, width, height } | null}
 */
export function panelFrame(frame) {
	if (frame?.kind !== "surface" || !frame.normal) return null;
	const n = norm(frame.normal);
	if (Math.abs(dot(n, UP)) > LEVEL_DOT) return null;
	const up = norm(sub(UP, scale(n, dot(UP, n))));
	const right = norm(cross(up, n));
	const ring = Array.isArray(frame.points) ? frame.points : [];
	if (ring.length < 3) return null;
	const p0 = ring[0];
	const rs = ring.map((p) => dot(sub(p, p0), right)), us = ring.map((p) => dot(sub(p, p0), up));
	const rMin = Math.min(...rs), uMin = Math.min(...us);
	return {
		normal: n, up, right,
		origin: add(add(p0, scale(right, rMin)), scale(up, uMin)),
		width: Math.max(...rs) - rMin, height: Math.max(...us) - uMin,
	};
}

/** The panel's cell lattice for one reference item: columns along `right` by the item's `along`
 *  extent + gap, rows along `up` by its `up` extent + gap. capacity = cols × rows (D-062 clause 1:
 *  derived, never authored — the number Content's mint and GCG must read the same way). */
export function panelGrid(panel, dims, pose, gap) {
	const cellW = packingSpan(dims, pose?.along, pose?.spacing) + gap;
	const cellH = (dims?.[pose?.up] ?? 0) + gap;
	const ok = cellW > gap && cellH > gap;
	const cols = ok ? Math.floor(panel.width / cellW + 1e-9) : 0;
	const rows = ok ? Math.floor(panel.height / cellH + 1e-9) : 0;
	return { cols, rows, cellW, cellH, gap };
}

// ─── The renderers ───────────────────────────────────────────────────────────────────────────

const FAN_STEP = 18 * Math.PI / 180;    // fan-spread's authored angle, kept so an arc socket that
                                        // replaces a fan-table's legacy layout does not re-cut the look.

/**
 * Render one grammar socket. Returns placements in WORLD coordinates.
 *
 * @returns {{ ok, distribution, pose, capacity, capacityReference, count, placements, frame, notes[] }}
 *          `ok:false` + `reason` when the socket is not renderable by the grammar (unknown or
 *          deferred word, missing pose preset, degenerate domain). The caller falls back to the
 *          legacy algorithm path — a socket the grammar cannot draw must not blank a shelf.
 */
export function layoutByGrammar({ socket, domain, approach, products = [], productTypes, maxItems = Infinity, seed = "", pins = [] } = {}) {
	const notes = [];
	const distribution = socket?.distribution;
	const poseName = socket?.pose;
	const fail = (reason) => ({ ok: false, reason, notes, placements: [], count: 0, capacity: 0 });

	if (!GRAMMAR_DISTRIBUTIONS.includes(distribution)) {
		return fail(GRAMMAR_DEFERRED.includes(distribution) ? `distribution '${distribution}' is named by D-062 but not built` : `unknown distribution '${distribution}'`);
	}
	const registry = productTypes instanceof Map ? productTypes : typeRegistry(productTypes);
	const ref = referenceType(socket, registry);
	if (!ref.entry) return fail(`accepts ${JSON.stringify(socket?.accepts || null)} resolves to no known product type`);
	if (!ref.matched.length) notes.push("accepts matched no tagPath; fell back to acceptedProductTypes");
	const pose = poseMapping(ref.entry, poseName);
	if (!pose) return fail(`product type '${ref.id}' declares no '${poseName}' pose`);

	const frame = domainFrame(domain, approach);
	if (!frame) return fail(`domain '${domain?.kind}' is not renderable (missing or degenerate geometry)`);
	if (frame.kind === "spline" && !(frame.length > 0)) return fail("spline domain has zero length");
	if (frame.kind === "volume" && distribution !== "stack") return fail(`'${distribution}' in a volume is not built (only 'stack')`);
	// A panel carries its own up (ASK-147 §5.4). Only `grid` is built on one: every other surface
	// distribution packs or rises along the NORMAL, which on a wall is straight out at the customer —
	// so it refuses by name and the adapter falls back, rather than drawing stock off the wall.
	const panel = panelFrame(frame);
	if (panel && distribution !== "grid") return fail(`'${distribution}' on a panel (a non-level surface) is not built (only 'grid')`);

	const face = norm(approach || V(0, 0, 1));
	const refDims = itemDims(null, ref.entry) || { w: 0.17, h: 0.25, d: 0.005, source: "fallback" };
	// D-064's mechanism, not its description: a socket whose `accepts` roots at the tag tree carries
	// NO dimensional opinion unless it declares `fit` — so a broad table would receive a piano. That
	// is a gate RED at the mint; here it is a loud note, because the client renders what it is given
	// and a silent wildcard is exactly the state D-064 exists to make impossible.
	if (!socket?.fit && rootsAtTagRoot(socket, registry)) {
		notes.push(`socket accepts the whole tag tree and declares no fit — D-064 requires one; nothing is refused by size here`);
	}
	const capacityReference = capacityAcross({ frame, distribution, poseName, socket, registry, ref, refPose: pose, refDims });
	if (Number.isFinite(socket?.capacity) && socket?.capacitySource === "derived" && socket.capacity !== capacityReference) {
		notes.push(`capacity ${capacityReference} derived here vs ${socket.capacity} published by the mint`);
	}

	// Along which direction does the run advance, and which way does an item face?
	//
	// When the packing axis IS the item's face axis (edge-out: `along` = `d` = the cover normal),
	// every item faces the customer and the run marches AWAY from them — the front-to-back browse
	// bin. Otherwise (face-out on a shelf) the run marches along the domain and the face is free to
	// point at the customer independently. Getting this from the pose rather than from the fixture
	// is what lets one renderer serve a bin, a shelf and a rail.
	const faceAxis = ref.entry?.canonicalFrame?.face || "d";
	const packIsFace = pose.along === faceAxis;
	// Start at the end nearest the customer so slot 0 is the item they see first (the browse bin's
	// front-to-back order) — and only when the run actually points at them. A shelf run crossing
	// the approach (dot 0) has no near end and keeps the domain's own order.
	const flipped = frame.kind === "spline" && packIsFace
		&& dot(sub(frame.points[frame.points.length - 1], frame.points[0]), face) > 1e-9;
	const tangent = flipped ? scale(frame.tangent, -1) : frame.tangent;
	const runOrigin = flipped ? frame.points[frame.points.length - 1] : frame.origin;
	const alongDir = packIsFace ? scale(tangent, -1) : tangent;   // the item's `along` axis direction
	const jitter = makeJitter(seed || socket?.id || "grammar");

	const placements = [];
	let saturated = false;             // scatter: the region turned stock away (see the branch below)
	const pool = Array.isArray(products) ? products : [];
	const nWanted = Math.min(pool.length, maxItems);
	// Each item is measured and posed as ITS OWN type: a book on a shared print shelf spends its
	// 30 mm at book spacing (1.0, a tight shelf) while a comic beside it spends 3.2 mm at 1.2.
	// The socket's reference type sets the capacity number and nothing else — which is why the
	// registry holds poses per type and the socket holds none.
	const itemOf = (product) => {
		const entry = (product?.type && registry.has(product.type)) ? registry.get(product.type) : ref.entry;
		const dims = itemDims(product, entry) || refDims;
		if (dims.suspect) notes.push(`item ${product?.id ?? "?"}: ${dims.suspect.reason} (×${dims.suspect.ratio}) — using the ${entry?.productTypeId || ref.id} reference`);
		return { entry, dims, pose: poseMapping(entry, poseName) || pose };
	};

	// On a panel the pose's `up` is the panel's up and the face is resolved toward +n (ASK-147 §5.4);
	// on a level surface it is the normal and the approach, exactly as before.
	const poseUp = panel ? panel.up : frame.normal;
	const poseFace = panel ? panel.normal : face;
	const place = (i, item, position, extra = {}) => {
		const { alongDir: itemAlong, ...rest } = extra;
		placements.push({
			index: i, product: pool[i] || null, dims: item.dims, dimsSource: item.dims.source, position,
			basis: poseBasis({ typeEntry: item.entry, pose: item.pose, normalDir: poseUp, alongDir: itemAlong || alongDir, faceDir: poseFace }),
			...rest,
		});
	};

	if (distribution === "single") {
		if (nWanted > 0) place(0, itemOf(pool[0]), frame.origin, { pitch: 0 });
	} else if (distribution === "run") {
		// Pack sequentially, each item spending its OWN packing dimension (D-062's derived capacity,
		// applied per item rather than per type): a lane holds more 3 mm floppies than 8 mm trades,
		// and mixed stock produces no overlap and no gap.
		const limit = frame.kind === "spline" ? frame.length : Infinity;
		const bent = frame.kind === "spline" && frame.points.length > 2;
		let cursor = 0;
		for (let i = 0; i < nWanted; i++) {
			const item = itemOf(pool[i]);
			const pitch = packingSpan(item.dims, item.pose.along, item.pose.spacing);
			if (!(pitch > 0)) { notes.push(`item ${i}: no ${item.pose.along} dimension — stopping the run`); break; }
			if (cursor + pitch > limit + 1e-9) { notes.push(`domain full after ${i} item(s) (${limit.toFixed(3)} m)`); break; }
			// The item's origin sits at the CENTRE of the cell it occupies, so the first item's
			// leading face is flush with the start of the domain — which is a physical wall.
			// ASK-150 §3.4 — a bent lane is walked by arc-length: the item stands ON its own segment and its
			// along-axis follows that segment, so an edge-out run turns the corner with the lane.
			const at = bent && polylineAt(frame.points, cursor + pitch / 2, flipped);
			place(i, item, at ? at.position : add(runOrigin, scale(tangent, cursor + pitch / 2)), at ? { pitch, alongDir: packIsFace ? scale(at.dir, -1) : at.dir } : { pitch });
			cursor += pitch;
		}
	} else if (distribution === "stack" && frame.kind === "volume") {
		// 🔴 ASK-139 F-139an — STOCK STANDING IN A BOX. The record-crate's bin: sleeves stand edge-out
		// (their `up` along the box's up, their packed axis — the cover normal — front-to-back), rows
		// march AWAY from the customer from the front wall exactly as the spline run does, and the box
		// is as many columns wide and layers high as the reference item allows. Each item spends its
		// OWN packing span (a 7" single beside a 12" LP), and is refused BY NAME if it is taller than
		// the box or wider than its column — never drawn through the wall.
		//
		// Fill order: round the columns at the front first (a crate with two records shows one at the
		// front of each column — the merchandising, not a lopsided bin), then deeper; a layer fills
		// before the next rises. Each placement says which cell it took (`cell {col,row,layer}`) and
		// how much room it has in front of and behind it (`room {front,back}`, metres along the depth)
		// — the browse gesture tips sleeves inside that room, so it is a fact of the layout, not a
		// guess of the renderer.
		const grid = volumeGrid(frame, refDims, pose);
		if (!grid.cols || !grid.layers) return fail(`reference ${ref.id} does not fit the volume`);
		const cells = grid.cols * grid.layers;
		const cursor = new Array(cells).fill(0), rows = new Array(cells).fill(0);
		let placed = 0;
		for (let i = 0; i < nWanted; i++) {
			const item = itemOf(pool[i]);
			const pitch = packingSpan(item.dims, item.pose.along, item.pose.spacing);
			if (!(pitch > 0)) { notes.push(`item ${pool[i]?.id ?? i}: no ${item.pose.along} dimension — skipped`); continue; }
			const tall = item.dims[item.pose.up] ?? 0, wide = item.dims[AXES.find((x) => x !== item.pose.up && x !== item.pose.along)] ?? 0;
			if (tall > frame.height + 1e-9 || wide > grid.cellWidth + 1e-9) {
				notes.push(`item ${pool[i]?.id ?? i}: ${wide} × ${tall} m — refused by fit`);
				continue;
			}
			let cell = -1;
			for (let layer = 0; layer < grid.layers && cell < 0; layer++) {
				for (let k = 0; k < grid.cols; k++) {
					const c = layer * grid.cols + ((placed + k) % grid.cols);
					if (cursor[c] + pitch <= frame.length + 1e-9) { cell = c; break; }
				}
			}
			if (cell < 0) { notes.push(`volume full after ${placed} item(s)`); break; }
			const col = cell % grid.cols, layer = Math.floor(cell / grid.cols);
			const at0 = cursor[cell];
			place(i, item, add(add(add(frame.origin, scale(frame.bitangent, (col + 0.5) * grid.cellWidth)), scale(tangent, at0 + pitch / 2)), scale(frame.normal, layer * grid.layerHeight)), {
				pitch, cell: { col, row: rows[cell], layer },
				room: { front: +at0.toFixed(6), back: +(frame.length - at0 - pitch).toFixed(6) },
			});
			cursor[cell] += pitch; rows[cell] += 1; placed += 1;
		}
	} else if (distribution === "stack") {
		// Items rise along the domain NORMAL, each spending its `pose.up` dimension — "pile" is
		// stack + jitter (D-062 §Axis 2), so the jitter is a parameter here, not another primitive.
		const jitterAmp = Number.isFinite(socket?.params?.jitter) ? socket.params.jitter : 0;
		const cap = capacityReference;
		const n = Math.min(nWanted, Number.isFinite(cap) && cap > 0 ? cap : nWanted);
		let cursor = 0;
		for (let i = 0; i < n; i++) {
			const item = itemOf(pool[i]);
			const pitch = packingSpan(item.dims, item.pose.up, item.pose.spacing);
			if (!(pitch > 0)) { notes.push(`item ${i}: no ${item.pose.up} dimension — stopping the stack`); break; }
			const wobble = jitterAmp ? add(scale(tangent, jitter() * jitterAmp), scale(cross(frame.normal, tangent), jitter() * jitterAmp)) : V(0, 0, 0);
			place(i, item, add(add(frame.origin, scale(frame.normal, cursor)), wobble), { pitch });
			cursor += pitch;
		}
	} else if (distribution === "arc") {
		// A fan: items sweep about the domain normal, centred on the approach, each turned to face
		// outward along its own radius. "Fanned" is an arc preset (D-062 §Axis 2) — the ±18° step is
		// the shipped fan-table look, kept so re-authoring that fixture as a grammar socket is not a
		// silent re-cut of a merchandising arrangement someone already signed off.
		const step = Number.isFinite(socket?.params?.angleStep) ? socket.params.angleStep : FAN_STEP;
		const cap = capacityReference;
		const n = Math.min(nWanted, Number.isFinite(cap) && cap > 0 ? cap : nWanted);
		const first = -((n - 1) / 2) * step;
		for (let i = 0; i < n; i++) {
			const item = itemOf(pool[i]);
			const dims = item.dims;
			const thirdAxis = AXES.find((x) => x !== item.pose.up && x !== item.pose.along);
			const ang = first + i * step;
			const rot = (v) => {                                  // Rodrigues about the domain normal
				const c = Math.cos(ang), s = Math.sin(ang), k = frame.normal;
				return add(add(scale(v, c), scale(cross(k, v), s)), scale(k, dot(k, v) * (1 - c)));
			};
			// Offsets are fan-spread's: a turned item is pushed sideways and toward the customer by
			// exactly enough that neighbouring bottom corners touch instead of overlapping. Written
			// against the pose's own in-plane axes (`along` × the third), so it holds for any pose.
			const a1 = dims[item.pose.along] ?? 0, a2 = dims[thirdAxis] ?? 0;
			const cA = Math.cos(ang), sA = Math.abs(Math.sin(ang));
			const offAlong = Math.sign(ang) * (a1 / 2 * (1 + cA) + a2 / 2 * sA);
			const offFace  = a2 / 2 * (1 - cA) + a1 / 2 * sA;
			place(i, item, add(add(frame.origin, scale(tangent, offAlong)), scale(face, offFace)),
				{ pitch: step, angle: ang, alongDir: rot(alongDir) });
		}
	} else if (distribution === "scatter") {
		// FREE PLACEMENT (ASK-121). Items go anywhere they fit inside the region, the merchant's pins
		// are honoured exactly where they were dropped, and the auto tiers pack AROUND them. This is
		// the distribution that makes "a merchant can drop a product anywhere that fits and the shop
		// restocks around it" true — and the reason the platform never emulates it with a dense set of
		// authored `point` sockets (ASK-121 §the design rule: The Sims 4 ships that, and it is the
		// anti-pattern this domain exists to avoid).
		if (frame.kind !== "surface") return fail(`scatter needs a surface domain, got '${frame.kind}'`);
		if (socket?.params?.leftover === "sparse") return fail("leftover policy 'sparse' is named but not built (ASK-120 ruling 7 — `dense` is the retail default and the only one implemented)");
		if (!frame.convex) notes.push("region is concave — footprints are edge-tested as well as corner-tested");
		const clearance = surfaceClearance(socket);
		const byKey = new Map();
		const items = [];
		for (let i = 0; i < nWanted; i++) {
			const item = itemOf(pool[i]);
			const adm = fitAdmits(socket?.fit, item.dims, item.pose);
			if (!adm.ok) { notes.push(`item ${pool[i]?.id ?? i}: ${adm.detail} — refused by fit`); continue; }
			const { fu, fv } = footprintOf(item.dims, item.pose);
			if (!(fu > 0) || !(fv > 0)) { notes.push(`item ${pool[i]?.id ?? i}: no footprint dimensions — skipped`); continue; }
			byKey.set(i, item);
			items.push({ key: i, fu, fv });
		}
		// A pin addresses a POINT in the region ­— `{u,v}` metres in the socket's own frame, the item's
		// base-centre. It is passed in by index so this stays pure: who is pinned is the feed's fact
		// (`via: 'pin'`), read by the adapter, never inferred here.
		const wanted = (Array.isArray(pins) ? pins : [])
			.filter((p) => byKey.has(p?.index) && Number.isFinite(p?.at?.u) && Number.isFinite(p?.at?.v))
			.map((p) => ({ key: p.index, u: p.at.u, v: p.at.v, yaw: Number.isFinite(p.at.yaw) ? p.at.yaw : 0 }));
		const packed = packScatter({ frame, items, pins: wanted, clearance });
		for (const r of packed.refused) notes.push(`item ${pool[r.key]?.id ?? r.key}: ${r.reason}`);
		// SATURATED — the region took everything it could and turned stock away. The reference capacity
		// above is what the socket's UMBRELLA type would fit; this is what THESE items actually spend,
		// and the two legitimately differ (a lane holds more thin comics than thick ones — the same
		// split `run` reports as capacityPacked). Saying which is which is the honest number.
		saturated = packed.refused.some((r) => r.reason === "no-fit");
		// Region order (u then v) so the panel's seat list and the shelf read the same way; a pinned
		// item keeps its place in that order rather than being hoisted to the front.
		const ordered = [...packed.placed].sort((a, b) => (a.v - b.v) || (a.u - b.u) || (a.key - b.key));
		ordered.forEach((p, slot) => {
			const item = byKey.get(p.key);
			const rot = rotateAboutAxis(frame.uAxis || frame.tangent, frame.normal, p.yaw);
			place(p.key, item, uvToWorld(frame, p.u, p.v), {
				pitch: p.fu, slot, yaw: p.yaw, pinned: p.pinned,
				at: { u: +p.u.toFixed(6), v: +p.v.toFixed(6), ...(p.yaw ? { yaw: +p.yaw.toFixed(6) } : {}) },
				footprint: { u: p.fu, v: p.fv }, alongDir: rot,
			});
		});
	} else if (distribution === "grid" && panel) {
		// 🔴 ASK-147 §5.4 — A GRID ON A PANEL: LPs face-out in a wall bay. The lattice is fixed by the
		// reference item (cell = its extent + the gap, cols × rows = the derived capacity); each item
		// stands upright in its cell — its `up` up the slope, its face out along +n — on the BOTTOM of
		// the cell (half a gap above the cell edge, the same half-gap that frames it left and right),
		// its back against the panel. Fill order: the bottom row first, left to right as the customer
		// sees it. An item bigger than a cell is refused BY NAME, never drawn into its neighbour.
		const gap = panelGridGap(socket);
		const g = panelGrid(panel, refDims, pose, gap);
		if (!g.cols || !g.rows) return fail(`reference ${ref.id} does not fit the panel (${panel.width.toFixed(3)} × ${panel.height.toFixed(3)} m)`);
		// The columns are centred across the bay (the stiles frame them evenly); the rows are not —
		// they stand on the bottom, where the lips are, and any spare height is left at the top.
		const inset = (panel.width - g.cols * g.cellW) / 2;
		let placed = 0;
		for (let i = 0; i < nWanted; i++) {
			const item = itemOf(pool[i]);
			const wide = packingSpan(item.dims, item.pose.along, item.pose.spacing), tall = item.dims[item.pose.up] ?? 0;
			if (!(wide > 0) || !(tall > 0)) { notes.push(`item ${pool[i]?.id ?? i}: no ${item.pose.along}/${item.pose.up} dimension — skipped`); continue; }
			if (wide > g.cellW - gap + 1e-9 || tall > g.cellH - gap + 1e-9) {
				notes.push(`item ${pool[i]?.id ?? i}: ${wide} × ${tall} m — refused by fit (cell ${(g.cellW - gap).toFixed(3)} × ${(g.cellH - gap).toFixed(3)} m)`);
				continue;
			}
			if (placed >= g.cols * g.rows) { notes.push(`grid full after ${placed} item(s)`); break; }
			const col = placed % g.cols, row = Math.floor(placed / g.cols);
			const proud = (item.dims[AXES.find((x) => x !== item.pose.up && x !== item.pose.along)] ?? 0) / 2;   // back on the panel
			place(i, item, add(add(add(panel.origin, scale(panel.right, inset + (col + 0.5) * g.cellW)), scale(panel.up, row * g.cellH + gap / 2)), scale(panel.normal, proud)),
				{ pitch: g.cellW, cell: { col, row }, alongDir: panel.right });
			placed += 1;
		}
	} else if (distribution === "grid") {
		// Rows × columns across a LEVEL 2D domain — jewellery in a case. Columns march along the
		// tangent by the item's `along` dimension, rows along the bitangent by whichever dimension is
		// left. A wall is the branch above (ASK-147 §5.4); this one is unchanged by it.
		if (frame.kind !== "surface") return fail(`grid needs a surface domain, got '${frame.kind}'`);
		const thirdAxis = AXES.find((x) => x !== pose.up && x !== pose.along);
		const colPitchRef = packingSpan(refDims, pose.along, pose.spacing);
		const rowPitchRef = packingSpan(refDims, thirdAxis, pose.spacing);
		const cols = colPitchRef > 0 ? Math.max(1, Math.floor(frame.length / colPitchRef)) : 1;
		for (let i = 0; i < nWanted; i++) {
			const item = itemOf(pool[i]);
			const col = i % cols, row = Math.floor(i / cols);
			const colPitch = packingSpan(item.dims, item.pose.along, item.pose.spacing) || colPitchRef;
			const rowPitch = packingSpan(item.dims, thirdAxis, item.pose.spacing) || rowPitchRef;
			if ((row + 1) * rowPitch > frame.width + 1e-9) { notes.push(`grid full after ${i} item(s)`); break; }
			place(i, item, add(add(frame.origin, scale(frame.tangent, col * colPitch + colPitch / 2)), scale(frame.bitangent, row * rowPitch + rowPitch / 2)),
				{ pitch: colPitch, cell: { col, row } });
		}
	}

	return {
		ok: true, distribution, pose: poseName, poseMapping: pose, typeId: ref.id,
		capacity: capacityReference, capacityPublished: Number.isFinite(socket?.capacity) ? socket.capacity : null,
		saturated, count: placements.length, placements, frame, notes,
		...(panel ? { panel } : {}),
	};
}

// ─── `at` — the PARAMETRIC pin address (ASK-120 §3.1) ────────────────────────────────────────
//
// A pin names a place in the socket's OWN domain coordinate, never an index into resolved output:
// output re-orders whenever the product mix changes (a lane holds 168 floppies or 81 trades —
// ASK-112 S4), so "the 5th item" is a different place in space each time. GCG stores and feeds `at`
// VERBATIM and never resolves it (D-061 §5); resolving it to a world position is the client's job,
// and this is where that happens — the domain frame is already computed right here.
//
//   null              any domain    "anywhere in this socket" — the resolver picks (the common case)
//   { ordinal: n }    point/spline  nth in the sequence — an ORDERING intent, moves in space, correctly
//   { pointId }       point         an authored spot (Houdini @id: author an immutable id, never an index)
//   { distance }      spline        metres from spline start (UE arc-length; survives a re-author)
//   { u, v, yaw? }    surface       metres in the region's own frame (ASK-121 — BUILT)
//   { u, v, w, pose? } volume       metres in the volume's frame   ── NOT BUILT

/** Domains whose `at` the platform can ADDRESS but not yet RENDER. Named here beside GRAMMAR_DEFERRED
 *  because it is the same discipline: a word the contract speaks and the renderer refuses BY NAME
 *  rather than guessing a position. `surface` LEFT this list in ASK-121 — the region renders, so the
 *  address resolves; `volume` stays until the 3D primitive does, and P11's refusal leg moves onto it. */
export const AT_DEFERRED_DOMAINS = Object.freeze(["volume"]);

/** Which address shape is this? Returns the discriminator, or null for "no opinion" (`at: null`). */
export function atKind(at) {
	if (at == null) return null;
	if (typeof at !== "object") return "invalid";
	if (Number.isFinite(at.u) && Number.isFinite(at.v)) return Number.isFinite(at.w) ? "volume-uvw" : "surface-uv";
	if (Number.isFinite(at.distance)) return "distance";
	if (Number.isFinite(at.ordinal)) return "ordinal";
	if (typeof at.pointId === "string" && at.pointId) return "pointId";
	return "invalid";
}

/** Domains each address shape is legal in (the per-domain validation GCG enforces with a 400). */
const AT_DOMAINS = Object.freeze({
	ordinal:     ["point", "spline"],
	pointId:     ["point"],
	distance:    ["spline"],
	"surface-uv": ["surface"],
	"volume-uvw": ["volume"],
});

/** Is `at` legal in this domain? `{ok}` or `{ok:false, reason:"at_domain_mismatch"|"at_malformed"}`. */
export function validateAt(at, domainKind) {
	const kind = atKind(at);
	if (kind === null) return { ok: true, kind: null };
	if (kind === "invalid") return { ok: false, kind, reason: "at_malformed" };
	const legal = AT_DOMAINS[kind] || [];
	if (domainKind && !legal.includes(domainKind)) return { ok: false, kind, reason: "at_domain_mismatch", legal };
	return { ok: true, kind };
}

/**
 * Can this address be RENDERED at all, given only the socket's declared domain? The frame-free half
 * of `resolveAtPosition`, for surfaces that must answer before any geometry is composed — the panel
 * badging a pin, and the drop-target admit check. Same refusal vocabulary, same never-guess rule.
 * @returns {{ok:true, kind}} | {{ok:false, kind, reason}}
 */
export function atRenderStatus(at, domainKind) {
	const v = validateAt(at, domainKind);
	if (!v.ok) return { ok: false, kind: v.kind, reason: v.reason, legal: v.legal };
	if (v.kind === null) return { ok: true, kind: null };
	if (AT_DEFERRED_DOMAINS.includes(domainKind)) return { ok: false, kind: v.kind, reason: "domain-not-built" };
	return { ok: true, kind: v.kind };
}

/**
 * Resolve a pin address to a WORLD position on an already-computed domain frame.
 *
 * REFUSES LOUDLY, never guesses. That is the whole point of the function existing before ASK-121:
 * a `{u,v}` address round-trips the wire verbatim today, and every consumer that cannot render it
 * must say `domain-not-built` by name — the same discipline `layoutByGrammar` applies to the
 * distributions D-062 names and nobody built (`GRAMMAR_DEFERRED`). A renderer that quietly picked a
 * plausible spot would make the address look supported and would have to be un-shipped later.
 *
 * @returns {{ok:true, position, kind}} | {{ok:false, reason, kind, detail?}}
 *          reasons: `domain-not-built` · `at_domain_mismatch` · `at_malformed` · `no-such-seat`
 *                 · `no-such-point` · `no-frame`
 */
export function resolveAtPosition({ frame, at, socket = null, itemPitch = 0 } = {}) {
	const kind = atKind(at);
	if (!frame) return { ok: false, kind, reason: "no-frame" };
	if (kind === null) return { ok: false, kind, reason: "at-unaddressed" };   // `null` = resolver's choice, not ours
	const v = validateAt(at, frame.kind);
	if (!v.ok) return { ok: false, kind, reason: v.reason, legal: v.legal };
	if (AT_DEFERRED_DOMAINS.includes(frame.kind)) {
		return { ok: false, kind, reason: "domain-not-built", detail: `'${frame.kind}' addressing is stored and fed verbatim, but the ${frame.kind} placement primitive is ASK-121 — refusing rather than guessing a position` };
	}
	if (kind === "pointId") {
		const pts = Array.isArray(socket?.geometry?.points) ? socket.geometry.points : [];
		const idx = pts.findIndex((p) => (p?.id ?? p?.pointId) === at.pointId);
		const world = idx >= 0 ? (frame.points?.[idx] ?? null) : null;
		return world ? { ok: true, kind, position: world } : { ok: false, kind, reason: "no-such-point" };
	}
	if (kind === "surface-uv") {
		// The address is metres in the socket's OWN frame (INTERFACE 2026-08-26 correction), so this is
		// one basis change — and then a REGION test, because a point on the plane is not the same thing
		// as a point on the table. Out of bounds is `no-such-seat`: the region was re-cut under a pin
		// the merchant set, and the honest answer is to report it, never to slide the item back inside.
		if (!Array.isArray(frame.polygonUV)) return { ok: false, kind, reason: "no-frame" };
		if (!frameContains(frame, at.u, at.v)) {
			const onBoard = insideUV(frame.polygonUV, at.u, at.v);
			return { ok: false, kind, reason: "no-such-seat",
				detail: `(${at.u.toFixed(3)}, ${at.v.toFixed(3)}) is ${onBoard ? "on a blocked patch of the region" : "outside the region"}` };
		}
		return { ok: true, kind, position: uvToWorld(frame, at.u, at.v), yaw: Number.isFinite(at.yaw) ? at.yaw : 0 };
	}
	if (kind === "distance") {
		if (frame.kind !== "spline") return { ok: false, kind, reason: "at_domain_mismatch" };
		if (!(at.distance >= 0) || at.distance > frame.length + 1e-9) return { ok: false, kind, reason: "no-such-seat" };
		return { ok: true, kind, position: splineAt(frame, at.distance) };   // ASK-150 §3.4
	}
	// ordinal — an ORDERING intent. On a point domain it indexes the authored points; on a spline it
	// is n item-pitches along, which is why the caller supplies the pitch rather than this guessing one.
	if (frame.kind === "point") {
		const p = frame.points?.[at.ordinal];
		return p ? { ok: true, kind, position: p } : { ok: false, kind, reason: "no-such-seat" };
	}
	if (!(itemPitch > 0)) return { ok: false, kind, reason: "no-fit", detail: "an ordinal on a spline needs the item's packing pitch" };
	const d = (at.ordinal + 0.5) * itemPitch;
	if (d > frame.length + 1e-9) return { ok: false, kind, reason: "no-such-seat" };
	return { ok: true, kind, position: frame.kind === "spline" ? splineAt(frame, d) : add(frame.origin, scale(frame.tangent, d)) };   // ASK-150 §3.4
}

/** The `grammarSource` values that mean "render from the socket". THREE values, and the middle one
 *  is the whole point of the split:
 *
 *    authored         somebody wrote the grammar for this socket on purpose
 *    derived-surface  the MINT measured a support-top and derived the grammar from geometry
 *                     (ASK-121 / P2-SOCKETS, live on `ca6ca8e`) — as authoritative as authored,
 *                     because D-061 clause 1 says derived-from-geometry BEATS hand-annotation
 *    derived          an S2 ANNOTATION of a legacy layout name — NOT grammar, and still refused
 *
 *  That last exclusion is why this is a list and not a truthiness test: S2 stamped a
 *  domain/distribution/pose onto every shipped fixture from its old layout name, and routing those
 *  through the grammar would re-cut a dozen live fixtures in one commit. `derived-surface` is a
 *  distinct word precisely so the mint's real derivation can be admitted without admitting those. */
export const GRAMMAR_SOURCES = Object.freeze(["authored", "derived-surface"]);

/** Does this socket declare a grammar the client should render from? */
export function isGrammarSocket(socket) {
	return GRAMMAR_SOURCES.includes(socket?.grammarSource)
		&& GRAMMAR_DISTRIBUTIONS.includes(socket?.distribution)
		&& GRAMMAR_DOMAINS.includes(socket?.domain)
		&& !!socket?.pose;
}
